import type { useRestClient } from '@/contexts/QueryProvider'
import { DMP_CHAT_CORRELATION_ID_HEADER, type DmpChatTaskBody } from './dmpChatTransport'
import {
  getDmpChatTaskResultStatus,
  getDmpChatUsage,
  normalizeDmpChatTaskStatus,
  type DmpChatTaskStatus,
  type DmpChatUsage,
} from './state/dmpChatState'

type DmpChatRestClient = ReturnType<typeof useRestClient>

export interface DmpChatAcceptedTask {
  id: string
  statusUrl: string | null
  resultUrl: string | null
}

export interface DmpChatTaskResultSnapshot {
  status: DmpChatTaskStatus | null
  message: string | null
  usage: DmpChatUsage | null
}

export interface DmpChatTaskAdapter {
  create(body: DmpChatTaskBody, requestId: string): Promise<DmpChatAcceptedTask>
  readStatus(taskId: string): Promise<DmpChatTaskStatus | null>
  readResult(taskId: string): Promise<DmpChatTaskResultSnapshot | null>
}

export function createDmpChatTaskAdapter(restClient: DmpChatRestClient): DmpChatTaskAdapter {
  return {
    async create(body, requestId) {
      const result = await restClient.postTasksApiTasksCreate({
        body,
        headers: {
          [DMP_CHAT_CORRELATION_ID_HEADER]: requestId,
        },
      } as Parameters<DmpChatRestClient['postTasksApiTasksCreate']>[0] & {
        headers: Record<string, string>
      })

      if (result.error || !result.data || typeof result.data.taskId !== 'number') {
        throw new Error('The Project Agent task was not accepted.')
      }

      return {
        id: String(result.data.taskId),
        statusUrl: result.data.statusUrl ?? null,
        resultUrl: result.data.resultUrl ?? null,
      }
    },

    async readStatus(taskId) {
      const numericTaskId = parseTaskId(taskId)
      if (numericTaskId === null) {
        return null
      }

      const result = await restClient.getTasksApiTasksGetStatus({
        parameters: {
          path: {
            taskId: numericTaskId,
          },
        },
      })
      if (result.error || !result.data) {
        return null
      }

      return normalizeDmpChatTaskStatus(result.data.status)
    },

    async readResult(taskId) {
      const numericTaskId = parseTaskId(taskId)
      if (numericTaskId === null) {
        return null
      }

      const result = await restClient.getTasksApiTasksGetResult({
        parameters: {
          path: {
            taskId: numericTaskId,
          },
        },
      })
      if (result.error || !result.data) {
        return null
      }

      return {
        status: getDmpChatTaskResultStatus(result.data.result) ?? normalizeDmpChatTaskStatus(result.data.status),
        message: getDmpChatTaskResultMessage(result.data.result),
        usage: getDmpChatUsage(result.data.result),
      }
    },
  }
}

function parseTaskId(taskId: string): number | null {
  const numericTaskId = Number(taskId)
  return Number.isSafeInteger(numericTaskId) && numericTaskId >= 0 ? numericTaskId : null
}

function getDmpChatTaskResultMessage(result: unknown): string | null {
  if (!result || typeof result !== 'object') {
    return null
  }

  const resultRecord = result as Record<string, unknown>
  const message = resultRecord.message ?? resultRecord.Message
  if (typeof message === 'string' && message.trim()) {
    return message
  }

  const nestedResult = resultRecord.result ?? resultRecord.Result
  return nestedResult ? getDmpChatTaskResultMessage(nestedResult) : null
}
